import { formatSize } from '@domain/product'
import { nomeDaSugestao } from './sugerir'
import type { Sugestao } from './sugerir'

type Props = {
  s: Sugestao
  onEscolher: (s: Sugestao) => void
}

/**
 * Uma linha da busca do campo "O que falta?".
 *
 * ## Tocar ADICIONA — não preenche o campo para você confirmar depois
 *
 * O desenho anterior punha o nome no campo e esperava um segundo toque no
 * botão "Adicionar". Eram dois gestos para uma intenção que já estava clara no
 * primeiro. Agora o toque grava, e o campo se limpa para o próximo item.
 *
 * ## O que já está na lista fica DESABILITADO, e diz por quê
 *
 * É a consequência direta de tocar adicionar na hora: sem o aviso, o segundo
 * toque criaria uma linha repetida em silêncio, e a pessoa só descobriria ao
 * chegar no mercado com "Coca-Cola" escrito duas vezes. Desabilitado em vez de
 * escondido — sumir faria parecer que a busca não achou.
 *
 * Só conta o que está **para comprar**: item concluído é compra feita, e
 * querer de novo é o caso normal da semana seguinte.
 *
 * ## Marca embaixo, medida na direita — e por que não tudo na mesma linha
 *
 * Visto em produção com "lys": quatro embalagens de Lysoform, e a única coisa
 * que as distinguia era o fim de um nome que a largura cortava. O que separa
 * uma da outra é **tamanho** e **marca**, e os dois estavam invisíveis.
 *
 * Emendar no nome não resolveria: "Lysoform desinfetante original 1 l × 4" é
 * mais texto para o mesmo `truncate` comer. Então a medida sai da linha do
 * nome e vai para a direita, onde tem coluna própria e alinha entre as linhas
 * — dá para varrer os tamanhos com o olho sem ler os nomes. A marca fica
 * embaixo, apagada: ela desempata, não identifica.
 */
export function LinhaDeSugestao({ s, onEscolher }: Props) {
  const ehGrupo = s.tipo === 'grupo'
  // "1 tamanho" não informa nada — mesma regra da lista de grupos.
  const detalhe = ehGrupo && s.tamanhos > 1 ? `${String(s.tamanhos)} tamanhos` : null
  const marca = ehGrupo ? null : s.produto.brand
  const medida = ehGrupo ? null : formatSize(s.produto.size, s.produto.unit, s.produto.packQuantity)

  return (
    <button
      type="button"
      disabled={s.naLista}
      onClick={() => {
        onEscolher(s)
      }}
      className="alvo w-full justify-between gap-3 border-b border-[var(--border)] px-1 text-left text-sm"
    >
      <span className="min-w-0 flex-1">
        <span
          className={`block truncate ${ehGrupo ? 'text-[var(--brand)]' : ''} ${
            s.naLista ? 'opacity-60' : ''
          }`}
        >
          {nomeDaSugestao(s)}
        </span>
        {detalhe !== null && (
          <span className="block text-xs text-[var(--text-faint)]">{detalhe}</span>
        )}
        {/* A marca desempata duas embalagens de nome parecido. Apagada porque
            não é ela que identifica — é o nome. */}
        {marca !== null && marca !== '' && (
          <span className="block truncate text-[11px] text-[var(--text-faint)]">{marca}</span>
        )}
      </span>

      {/* Coluna própria: alinhada entre as linhas, dá para varrer os tamanhos
          sem ler os nomes. `tabular-nums` para os dígitos não dançarem. */}
      {medida !== null && (
        <span className="shrink-0 text-xs tabular-nums text-[var(--text-muted)]">{medida}</span>
      )}

      <span
        className={`shrink-0 text-xs ${
          s.naLista ? 'text-[var(--positive)]' : 'text-[var(--text-faint)]'
        }`}
      >
        {s.naLista ? '✓ na lista' : '+'}
      </span>
    </button>
  )
}
